<template>
  <v-menu
    v-model="show"
    absolute content-class="user-menu"
    transition="slide-y-reverse-transition"
    :close-delay="0"
    :nudge-width="250"
    :position-x="x"
    :position-y="y"
  >
    <v-card v-if="target" color="grey600" class="rounded-0">
      <v-list dense color="blue800" class="py-1 rounded-0">
        <v-list-item>
          <v-list-item-content>
            <Username class="font-weight-semibold text-body-2 blue200--text" :user="target" />
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-list
        v-if="user"
        dense color="grey600" class="rounded-0 mod-menu-list"
      >
        <!-- Delete message -->
        <v-list-item
          v-if="target.messageId && $hasPermission(user.permissions, 'CAN_MUTE_USERS')"
          :ripple="false" @click="deleteMessage(target.messageId, target.room)"
        >
          <v-list-item-action class="d-flex mr-3 flex-center">
            <fai :icon="['fad', 'trash']" fixed-width class="icon title primary500--text" />
          </v-list-item-action>
          <v-list-item-title>Delete chat message</v-list-item-title>
        </v-list-item>

        <!-- Mute -->
        <v-list-item
          v-if="target.room && $hasPermission(user.permissions, 'CAN_MUTE_USERS')"
          :ripple="false" @click="purge(target.steamid, target.room)"
        >
          <v-list-item-action class="d-flex mr-3 flex-center">
            <fai :icon="['fad', 'comment-slash']" fixed-width class="icon title primary500--text" />
          </v-list-item-action>
          <v-list-item-title> Purge Messages </v-list-item-title>
        </v-list-item>

        <!-- Mute -->
        <v-list-item
          v-if="$hasPermission(user.permissions, 'CAN_MUTE_USERS')"
          :ripple="false"
        >
          <v-list-item-action class="d-flex mr-3 flex-center" @click.prevent.stop>
            <fai :icon="['fad', 'ban']" fixed-width class="icon title primary500--text" />
          </v-list-item-action>
          <v-list-item-title class="d-flex flex-center-y" @click.prevent.stop>
            Mute
            <v-spacer />
            <v-btn x-small class="px-1 ml-2 mr-1 unwidth font-weight-bold" color="primary600" @click="mute(target.steamid, '15m')">
              15m
            </v-btn>
            <v-btn x-small class="px-1 mr-1 unwidth font-weight-bold" color="primary600" @click="mute(target.steamid, '1h')">
              1h
            </v-btn>
            <v-btn x-small class="px-1 mr-1 unwidth font-weight-bold" color="primary600" @click="mute(target.steamid, '1d')">
              1d
            </v-btn>
            <v-btn x-small class="px-2 unwidth font-weight-bold" color="primary600" @click="mute(target.steamid)">
              Custom
            </v-btn>
          </v-list-item-title>
        </v-list-item>

        <!-- Unmute -->
        <v-list-item
          v-if="$hasPermission(user.permissions, 'CAN_MUTE_USERS')"
          :ripple="false" @click="unmute(target.steamid)"
        >
          <v-list-item-action class="d-flex mr-3 flex-center">
            <fai :icon="['fas', 'check']" fixed-width class="icon title primary500--text" />
          </v-list-item-action>
          <v-list-item-title>Unmute</v-list-item-title>
        </v-list-item>

        <!-- Copy id -->
        <v-list-item
          v-if="$hasPermission(user.permissions, 'CAN_MUTE_USERS')"
          :ripple="false" @click="copy(target.steamid)"
        >
          <v-list-item-action class="d-flex mr-3 flex-center">
            <fai :icon="['fad', 'copy']" fixed-width class="icon title primary500--text" />
          </v-list-item-action>
          <v-list-item-title>Copy steamid</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex'
import ms from 'ms'

export default {
  data() {
    return {
      show: false,
      x: 0,
      y: 0,
      target: undefined,
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/session',
    }),
  },
  created() {
    this.$on('inspect', async ({ e, user }) => {
      e.preventDefault()

      this.show = false
      this.target = user
      this.x = e.clientX
      this.y = e.clientY

      await this.$nextTick()
      this.show = true

      // HACK
      setTimeout(() => {
        this.show = true
      }, 300)
    })
  },
  methods: {
    async deleteMessage(id, room) {
      try {
        await this.$socket.request('admin.chat.delete', { id, room })
        this.$toast.info('Message deleted successfully.')
      } catch (err) {
        this.$toast.error(err.message)
      }
    },
    async purge(steamid, room) {
      try {
        await this.$socket.request('admin.chat.purge', { steamid, room })
        this.$toast.info('User purged successfully.')
      } catch (err) {
        this.$toast.error(err.message)
      }
    },
    async mute(steamid, duration) {
      const form = {
        steamid,
        reason: 'Muted by a moderator.',
      }

      if (!duration) {
        // use modal
        const modal = this.$modal.open(
          'admin-user-ban',
          { hasReturnValue: true, maxWidth: 400 },
          { user: this.target, type: 'chat' },
        )
        const modalForm = await modal.promise

        if (!modalForm) return

        form.duration = modalForm.duration
        form.reason = modalForm.reason
      } else {
        form.duration = ms(duration)
      }

      try {
        await this.$socket.request('admin.chat.mute', form)
        this.$toast.info(`User timed out for ${ms(form.duration, { long: true })}.`)
      } catch (err) {
        this.$toast.error(err.message)
      }
    },
    async unmute(steamid) {
      try {
        await this.$socket.request('admin.chat.unmute', steamid)
        this.$toast.info('User unmuted successfully.')
      } catch (err) {
        this.$toast.error(err.message)
      }
    },
    async copy(val) {
      if (!navigator.clipboard) {
        this.$toast.open({ type: 'error', text: 'Your browser doesn\'t support this feature.' })
        return
      }

      try {
        await navigator.clipboard.writeText(val)

        this.$toast.info('Copied')
      } catch (err) {
        this.$toast.error('Couldn\'t copy, sorry.')
      }
    },
    steamProfile(steamid) {
      const url = `https://steamcommunity.com/profiles/${steamid}`
      const win = window.open(url, '_blank')
      win.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
.user-menu {
  z-index: 400 !important;
}

::v-deep .v-list-item::after {
  display: none;
}

.mod-menu-list:empty {
  display: none;
  padding: 0;
}
</style>
